import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { X, User, Baby } from "lucide-react";

const Leaderboard = () => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState("leaderboard");
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({
    parentName: "",
    email: "",
    phone: "",
    city: "",
    country: "",
    childName: "",
    childAge: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCompletedProfile, setHasCompletedProfile] = useState(false);

  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await fetch("https://sochu.online/api/leaderboard");
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch leaderboard", err);
      }
    };
    fetchLeaderboard();
  }, []);

  // Check if user has completed profile
  useEffect(() => {
    const checkProfile = async () => {
      if (!user) return;
      try {
        const res = await fetch(`https://sochu.online/api/user-profile/${user.id}`);
        const data = await res.json();
        if (data.profileCompleted) setHasCompletedProfile(true);
      } catch (err) {
        console.error("Failed to check profile status", err);
      }
    };
    checkProfile();
  }, [user]);

  const handleFooterClick = (page) => {
    setSelected(page);
    navigate(`/${page}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    if (!user) {
      alert("Please sign in to complete your profile");
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await fetch(`https://sochu.online/api/user-profile/${user.id}/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...profileData, userId: user.id }),
      });

      if (response.ok) {
        setHasCompletedProfile(true);
        setShowProfileModal(false);
        alert("Profile completed successfully! 🎉");
        setProfileData({
          parentName: "",
          email: "",
          phone: "",
          city: "",
          country: "",
          childName: "",
          childAge: "",
        });
      } else {
        alert("Failed to save profile. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting profile:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="p-4 bg-white">
        {/* Complete Profile Button */}
        {!hasCompletedProfile && (
          <div className="mb-4">
            <button
              onClick={() => setShowProfileModal(true)}
              className="w-full bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white font-bold py-3 px-6 rounded-xl shadow-md transition duration-200 border-b-4 border-green-600 active:translate-y-1"
            >
              ✨ Complete Your Profile
            </button>
          </div>
        )}

        <div className="text-center mb-4">
          <div className="flex flex-col items-center justify-center mb-2">
            <img
              src="/leaderboardv2.svg"
              alt="Medal"
              className="w-24 h-24 pb-3"
            />
            <h2 className="text-2xl font-bold mb-2">Leaderboard</h2>
            <p className="text-sm text-gray-500">
              See where you stand among other learners in the community.
            </p>
          </div>
          <hr className="border-gray-300 my-2" />
        </div>

        {users.length === 0 ? (
          <p className="text-center text-gray-500">No users yet</p>
        ) : (
          <div className="space-y-2">
            {users.map((user, index) => (
              <div
                key={user.userId || index}
                className="flex items-center justify-between bg-white p-4 rounded-xl shadow-md"
              >
                <div className="flex items-center gap-3">
                  <div className="text-xl font-bold text-blue-600">{index + 1}</div>
                  <div className="rounded-full bg-purple-500 w-10 h-10 flex items-center justify-center text-white text-lg font-semibold">
                    {user.name ? user.name.charAt(0).toUpperCase() : "A"}
                  </div>
                  <div>
                    <div className="text-lg font-medium">
                      {user.childname || "Anonymous"}
                    </div>
                  </div>
                </div>
                <div className="text-xl font-bold text-gray-800">{user.xp} XP</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md h-[90vh] flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">Complete Your Profile</h2>
              <button
                onClick={() => setShowProfileModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Form */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              <form onSubmit={handleSubmitProfile} className="space-y-6 pb-20">
                {/* Parent Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <User size={20} className="text-green-500" />
                    Parent/Guardian's Details
                  </h3>

                  <div className="space-y-4">
                    {["parentName", "email", "phone", "city", "country"].map((field, i) => (
                      <div key={i}>
                        <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                          {field.replace(/([A-Z])/g, " $1")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                          name={field}
                          value={profileData[field]}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder={`Enter your ${field}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Child Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Baby size={20} className="text-pink-500" />
                    Child's Details
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="childName"
                        value={profileData.childName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Child's name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Age <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="childAge"
                        value={profileData.childAge}
                        onChange={handleInputChange}
                        required
                        min="0"
                        max="18"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Child's age"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button (Fixed at bottom but within modal) */}
                <div className="sticky bottom-0 bg-white pt-4 pb-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-xl shadow-md transition duration-200 border-b-4 border-green-600 active:translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Saving..." : "Complete Profile"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-md p-2 pt-3 z-50" style={{ boxShadow: "0 -1px 4px rgba(0, 0, 0, 0.05)" }}>
        <div className="flex justify-around items-center text-gray-600 pb-3">
          <button
            onClick={() => {
              const audio = new Audio("/sounds/click.mp3");
              audio.play();
              handleFooterClick("learn");
            }}
            className={`flex flex-col items-center ${
              selected === "broadcasts" ? "text-blue-600" : "text-gray-600"
            } transition-colors`}
          >
            <img
              src={
                selected === "learn"
                  ? "/sochumenuselectednew.png"
                  : "/sochumenuunselectednew.png"
              }
              alt="Broadcast"
              className="h-14 w-14"
            />
            <span className="text-xs">Learn</span>
          </button>

          <button
            onClick={() => {
              const audio = new Audio("/sounds/click.mp3");
              audio.play();
              handleFooterClick("leaderboard");
            }}
            className={`flex flex-col items-center ${
              selected === "leaderboard" ? "text-yellow-600" : "text-gray-600"
            } transition-colors`}
          >
            <img
              src={
                selected === "leaderboard"
                  ? "/manjumenuselectednew.png"
                  : "/manjumenuunselectednew.png"
              }
              alt="Leaderboard"
              className="h-14 w-14"
            />
            <span className="text-xs">Leaderboard</span>
          </button>

          <button
            onClick={() => {
              const audio = new Audio("/sounds/click.mp3");
              audio.play();
              handleFooterClick("bedtime-stories");
            }}
            className={`flex flex-col items-center ${
              selected === "entertainment" ? "text-green-600" : "text-gray-600"
            } transition-colors`}
          >
            <img
              src={
                selected === "bedtime"
                  ? "/robermenuselected.png"
                  : "/robertmenuunselected.png"
              }
              alt="Entertainment"
              className="h-14 w-14"
            />
            <span className="text-xs">Bedtime</span>
          </button>

          <button
            onClick={() => {
              const audio = new Audio("/sounds/click.mp3");
              audio.play();
              handleFooterClick("setting");
            }}
            className={`flex flex-col items-center ${
              selected === "learn" ? "text-pink-600" : "text-gray-600"
            } transition-colors`}
          >
            <img
              src={
                selected === "setting"
                  ? "/anjumenuunselectednew.png"
                  : "/anjumenuselectednew.png"
              }
              alt="Learn"
              className="h-14 w-14"
            />
            <span className="text-xs">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;

